import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/auth/auth-store';
import { api } from '@/services/api/client';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Loader2 } from 'lucide-react';
import * as errorUtils from '@/utils/errors';
import {
  getBrowserNotificationPermission,
  requestBrowserNotificationPermission,
} from '@/utils/browserNotifications';

interface NotificationSettings {
  emailEnabled: boolean;
  emailTypes: {
    securityAlerts: boolean;
    productUpdates: boolean;
    marketing: boolean;
  };
  pushEnabled: boolean;
  desktopEnabled: boolean;
}

type NotificationSettingPath =
  | 'emailEnabled'
  | 'emailTypes.securityAlerts'
  | 'emailTypes.productUpdates'
  | 'emailTypes.marketing'
  | 'pushEnabled'
  | 'desktopEnabled';

export const NotificationsTab: React.FC = () => {
  const { user, setUser } = useAuthStore();
  const [isSaving, setIsSaving] = useState(false);
  const [desktopPermission, setDesktopPermission] = useState<NotificationPermission>(
    getBrowserNotificationPermission()
  );
  const [settings, setSettings] = useState<NotificationSettings>({
    emailEnabled: true,
    emailTypes: {
      securityAlerts: true,
      productUpdates: true,
      marketing: false,
    },
    pushEnabled: false,
    desktopEnabled: false,
  });

  useEffect(() => {
    if (user?.preferences?.notifications) {
      setSettings({
        emailEnabled: user.preferences.notifications.email,
        emailTypes: {
          securityAlerts: true, // Always enabled for security
          productUpdates: user.preferences.notifications.email,
          marketing: false, // TODO: Add marketing preference to user model
        },
        pushEnabled: false, // TODO: Add push notifications to user model
        desktopEnabled: user.preferences.notifications.desktop,
      });
    }
  }, [user]);

  const handleToggle = (path: NotificationSettingPath, value: boolean) => {
    setSettings((prev) => {
      if (path === 'emailTypes.securityAlerts') {
        return {
          ...prev,
          emailTypes: { ...prev.emailTypes, securityAlerts: value },
        };
      }

      if (path === 'emailTypes.productUpdates') {
        return {
          ...prev,
          emailTypes: { ...prev.emailTypes, productUpdates: value },
        };
      }

      if (path === 'emailTypes.marketing') {
        return {
          ...prev,
          emailTypes: { ...prev.emailTypes, marketing: value },
        };
      }

      return {
        ...prev,
        [path]: value,
      };
    });
  };

  const handleDesktopToggle = async (checked: boolean) => {
    if (!checked) {
      handleToggle('desktopEnabled', false);
      return;
    }

    const permission = await requestBrowserNotificationPermission();
    setDesktopPermission(permission);

    if (permission === 'granted') {
      handleToggle('desktopEnabled', true);
      return;
    }

    handleToggle('desktopEnabled', false);
    toast.error(
      permission === 'denied'
        ? 'Browser notifications are blocked. Update your browser permissions to enable them.'
        : 'Browser notifications are not available in this browser.'
    );
  };

  const handleSave = async () => {
    if (!user) return;

    setIsSaving(true);
    try {
      // Call the real API to update notification preferences
      const response = await api.put<{ user: typeof user }>('/users/me/preferences', {
        notifications: {
          email: settings.emailEnabled,
          desktop: settings.desktopEnabled,
          collaboration: user.preferences?.notifications?.collaboration ?? true,
        },
      });

      // Update user in store with response data or fallback
      if (response.data?.user) {
        setUser(response.data.user);
      } else {
        setUser({
          ...user,
          preferences: {
            ...user.preferences,
            notifications: {
              ...user.preferences.notifications,
              email: settings.emailEnabled,
              desktop: settings.desktopEnabled,
            },
          },
        });
      }

      toast.success('Notification preferences saved successfully');
    } catch (error: unknown) {
      const message = errorUtils.getErrorMessageOrFallback(error, 'Failed to save notification preferences. Please try again.');
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Email Notifications Section */}
      <Card>
        <CardHeader>
          <CardTitle>Email Notifications</CardTitle>
          <CardDescription>
            Choose what updates you want to receive via email
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="email-enabled" className="text-base font-medium">
                Email Notifications
              </Label>
              <p className="text-sm text-muted-foreground">
                Receive notifications via email
              </p>
            </div>
            <Switch
              id="email-enabled"
              checked={settings.emailEnabled}
              onCheckedChange={(checked) => handleToggle('emailEnabled', checked)}
            />
          </div>

          {settings.emailEnabled && (
            <>
              <Separator />

              <div className="space-y-4">
                <h4 className="text-sm font-medium">Email Types</h4>

                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label
                      htmlFor="security-alerts"
                      className="text-sm font-normal"
                    >
                      Security Alerts
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Important security updates and alerts about your account
                    </p>
                  </div>
                  <Switch
                    id="security-alerts"
                    checked={settings.emailTypes.securityAlerts}
                    onCheckedChange={(checked) =>
                      handleToggle('emailTypes.securityAlerts', checked)
                    }
                    disabled
                    aria-label="Security alerts are always enabled"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label
                      htmlFor="product-updates"
                      className="text-sm font-normal"
                    >
                      Product Updates
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      New features, improvements, and product announcements
                    </p>
                  </div>
                  <Switch
                    id="product-updates"
                    checked={settings.emailTypes.productUpdates}
                    onCheckedChange={(checked) =>
                      handleToggle('emailTypes.productUpdates', checked)
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="marketing" className="text-sm font-normal">
                      Marketing
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Tips, case studies, and promotional content
                    </p>
                  </div>
                  <Switch
                    id="marketing"
                    checked={settings.emailTypes.marketing}
                    onCheckedChange={(checked) =>
                      handleToggle('emailTypes.marketing', checked)
                    }
                  />
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Push Notifications Section */}
      <Card>
        <CardHeader>
          <CardTitle>Push Notifications</CardTitle>
          <CardDescription>
            Receive push notifications on your mobile devices
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="push-enabled" className="text-base font-medium">
                Push Notifications
              </Label>
              <p className="text-sm text-muted-foreground">
                Enable push notifications for real-time updates
              </p>
            </div>
            <Switch
              id="push-enabled"
              checked={settings.pushEnabled}
              onCheckedChange={(checked) => handleToggle('pushEnabled', checked)}
            />
          </div>

          {settings.pushEnabled && (
            <>
              <Separator className="my-4" />
              <div className="rounded-lg bg-muted p-4">
                <p className="text-sm text-muted-foreground">
                  Push notifications are not yet configured. Install the TerraMatrix
                  mobile app to receive push notifications.
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Desktop Notifications Section */}
      <Card>
        <CardHeader>
          <CardTitle>Desktop Notifications</CardTitle>
          <CardDescription>
            Receive notifications on your desktop browser
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="desktop-enabled" className="text-base font-medium">
                Desktop Notifications
              </Label>
              <p className="text-sm text-muted-foreground">
                Show notifications while using TerraMatrix in your browser
              </p>
            </div>
            <Switch
              id="desktop-enabled"
              checked={settings.desktopEnabled}
              onCheckedChange={handleDesktopToggle}
            />
          </div>

          {settings.desktopEnabled && (
            <>
              <Separator className="my-4" />
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Desktop notifications will appear when:
                </p>
                <ul className="ml-4 list-disc space-y-1 text-sm text-muted-foreground">
                  <li>Someone collaborates on your project</li>
                  <li>You receive a new message or mention</li>
                  <li>Important system updates are available</li>
                  <li>A background process completes</li>
                </ul>
              </div>
            </>
          )}
          {!settings.desktopEnabled && desktopPermission === 'denied' && (
            <p className="mt-4 text-sm text-muted-foreground">
              Browser notifications are blocked in this browser.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Collaboration Notifications */}
      <Card>
        <CardHeader>
          <CardTitle>Collaboration Notifications</CardTitle>
          <CardDescription>
            Notifications about real-time collaboration activities
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label
                htmlFor="collaboration-enabled"
                className="text-base font-medium"
              >
                Collaboration Alerts
              </Label>
              <p className="text-sm text-muted-foreground">
                Get notified when others join or edit your projects
              </p>
            </div>
            <Switch
              id="collaboration-enabled"
              checked={user?.preferences?.notifications?.collaboration ?? true}
              onCheckedChange={(checked) => {
                if (!user) return;
                setUser({
                  ...user,
                  preferences: {
                    ...user.preferences,
                    notifications: {
                      ...user.preferences.notifications,
                      collaboration: checked,
                    },
                  },
                });
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end gap-3">
        <Button
          variant="outline"
          onClick={() => {
            if (user?.preferences?.notifications) {
              setSettings({
                emailEnabled: user.preferences.notifications.email,
                emailTypes: {
                  securityAlerts: true,
                  productUpdates: user.preferences.notifications.email,
                  marketing: false,
                },
                pushEnabled: false,
                desktopEnabled: user.preferences.notifications.desktop,
              });
            }
          }}
        >
          Reset
        </Button>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </div>
    </div>
  );
};
